import { createApp } from 'vue';
import CardGrid from './CardGrid.vue';
import { normalizePayload } from './cards.js';

const mounts = new WeakMap();
let mountSequence = 0;

function ownedChild(element, selector) {
    return [...element.querySelectorAll(selector)].find(child => child.closest('[data-card-grid]') === element);
}

/** Enhance server-rendered card grids, preserving their fallback on invalid data. */
export function mountCardGrids(root = document) {
    const elements = [...root.querySelectorAll('[data-card-grid]')];
    if (root.matches?.('[data-card-grid]')) elements.unshift(root);

    return elements.flatMap(element => {
        if (mounts.has(element)) return [mounts.get(element)];
        const data = ownedChild(element, 'script[data-card-grid-data]');
        const target = ownedChild(element, '[data-card-grid-mount]');
        if (!data || !target) return [];

        let props;
        try {
            props = normalizePayload(JSON.parse(data.textContent));
        } catch {
            return [];
        }

        const fallback = [...target.childNodes].map(node => node.cloneNode(true));
        const app = createApp(CardGrid, props);
        app.config.idPrefix = `cards-${++mountSequence}`;
        try {
            app.mount(target);
        } catch {
            app.unmount();
            target.replaceChildren(...fallback);
            return [];
        }

        let mounted = true;
        const unmount = () => {
            if (!mounted) return;
            mounted = false;
            app.unmount();
            target.replaceChildren(...fallback);
            mounts.delete(element);
        };
        mounts.set(element, unmount);
        return [unmount];
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountCardGrids(), { once: true });
} else {
    mountCardGrids();
}
